/**
 * Shared building blocks for the two admin user-management pages
 * (/admin/staff and /admin/clients). The actions themselves operate
 * by user_id and don't care whether the target is staff or a client —
 * audience-specific differences live in the page (which `allowedActions`
 * subset it passes, which columns it renders).
 */
import { useEffect, useState } from 'react';
import { tokens } from '../../components/forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { fullName } from '../../lib/fullName';
import { cleanMfaInput, isMfaCode, stepUpErrorMessage } from '../../lib/mfaApproval';
import { ROLE_LABEL } from '../../lib/roleRouting';
import type { BadgeTone } from '../../components/StatusBadge';
import adminService, {
  type AdminUserSummary,
  type StaffChangeKind,
  type StaffRole,
  type UserRole,
} from '../../services/admin.service';
import { useMovablePopup } from '../../components/MovablePopup';
import { needsSecondAdmin } from './staffChanges.helpers';

export type ActionKind =
  | 'deactivate'
  | 'activate'
  | 'force_password'
  | 'unlock'
  | 'revoke_sessions'
  | 'change_role'
  | 'change_timezone'
  | 'reassign_clients'
  | 'totp_reset'
  | 'assign_manager';

export interface ActionState {
  kind: ActionKind;
  target: AdminUserSummary;
}

/** Shortlist exposed in the timezone picker. Free-text below the
 *  picker handles anything not on the list. Mexico entries are one per
 *  DISTINCT behavior since the 2022 DST abolition: Mexico_City covers the
 *  whole fixed UTC-6 interior (Monterrey, Chihuahua, ...); the rest differ
 *  (Cancun UTC-5, Hermosillo fixed UTC-7, Tijuana UTC-8 + US DST). */
export const COMMON_TIMEZONES = [
  'America/Mexico_City',
  'America/Tijuana',
  'America/Cancun',
  'America/Hermosillo',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'UTC',
];

export const ROLE_TONE: Record<UserRole, BadgeTone> = {
  super_admin: 'warning',
  manager:     'info',
  agent:       'info',
  client:      'neutral',
};

/** Roles that `change_role` can target. Staff stay on the staff side;
 *  demoting to / promoting from `client` is intentionally disallowed
 *  (memory: invitations_client_only — staff path and client path never
 *  cross). A change to or from manager or super admin is proposed and confirmed by a
 *  different super admin (#326). */
export const ROLE_OPTIONS: StaffRole[] = ['super_admin', 'manager', 'agent'];

/** What the dialog proposes, for the actions that take two super admins. */
const PROPOSED_KIND: Partial<Record<ActionKind, StaffChangeKind>> = {
  deactivate: 'deactivate',
  activate: 'reactivate',
  change_role: 'change_role',
};

const PROPOSED_MESSAGE =
  'Proposed. A different super admin confirms it under Changes waiting for a second super admin.';

/** Does this action on this account take two super admins (the dialog proposes it)? */
export function proposesChange(kind: ActionKind, target: AdminUserSummary, newRole?: UserRole): boolean {
  if (kind === 'change_role') return needsSecondAdmin(target.role, newRole);
  return PROPOSED_KIND[kind] !== undefined && needsSecondAdmin(target.role);
}

export function extractError(e: unknown): string {
  return getApiErrorMessage(e, 'Unexpected error');
}

// ─────────────────────────────────────────────────────────────────
//  RowMenu — the per-row "Actions ▾" dropdown.
//  The page decides which action kinds are allowed for its audience;
//  the dropdown still applies row-level safety guards (disabling
//  destructive items on super_admin rows, hiding agent-specific items
//  for non-agents, hiding force_password for Google SSO users).
// ─────────────────────────────────────────────────────────────────

export function RowMenu({
  user,
  allowedActions: allowed,
  onPick,
}: Readonly<{
  user: AdminUserSummary;
  allowedActions: ReadonlySet<ActionKind>;
  onPick: (kind: ActionKind) => void;
}>): JSX.Element {
  const [open, setOpen] = useState(false);
  const isSuperAdmin = user.role === 'super_admin';
  // A staff role changes only to another staff role; for a manager or a super admin the dialog
  // proposes it and a different super admin confirms it.
  const roleChangeDisabled = user.role === 'client';
  // A timezone is edited for an agent or a manager only; the backend refuses any other row.
  const timezoneChangeDisabled = isSuperAdmin || user.role === 'client';

  const pick = (kind: ActionKind): void => {
    onPick(kind);
    setOpen(false);
  };

  return (
    <div className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-1.5 rounded-md text-xs font-semibold"
        style={{ background: tokens.surfaceMuted, color: tokens.textPrimary }}
      >
        Actions ▾
      </button>
      {open && (
        <div
          role="menu"
          tabIndex={-1}
          className="absolute right-0 mt-1 z-10 w-56 rounded-lg shadow-lg py-1"
          style={{ background: tokens.surface, border: `1px solid ${tokens.border}` }}
          onMouseLeave={() => setOpen(false)}
        >
          {allowed.has('deactivate') && user.is_active && (
            <MenuItem label="Deactivate" danger onClick={() => pick('deactivate')} />
          )}
          {allowed.has('activate') && !user.is_active && !user.is_system_account && (
            <MenuItem label="Activate" onClick={() => pick('activate')} />
          )}
          {allowed.has('force_password') && (
            <MenuItem
              label="Force password change"
              disabled={user.auth_method !== 'local'}
              onClick={() => pick('force_password')}
            />
          )}
          {allowed.has('unlock') && (
            <MenuItem label="Unlock account" onClick={() => pick('unlock')} />
          )}
          {allowed.has('revoke_sessions') && (
            <MenuItem label="Revoke all sessions" onClick={() => pick('revoke_sessions')} />
          )}
          {allowed.has('change_role') && (
            <MenuItem
              label="Change role…"
              disabled={roleChangeDisabled}
              onClick={() => pick('change_role')}
            />
          )}
          {allowed.has('change_timezone') && (
            <MenuItem
              label="Change timezone…"
              disabled={timezoneChangeDisabled}
              onClick={() => pick('change_timezone')}
            />
          )}
          {allowed.has('reassign_clients') && user.role === 'agent' && (
            <MenuItem label="Reassign clients…" onClick={() => pick('reassign_clients')} />
          )}
          {allowed.has('assign_manager') && user.role === 'agent' && (
            <MenuItem label="Assign manager…" onClick={() => pick('assign_manager')} />
          )}
          {allowed.has('totp_reset') && (
            <MenuItem label="Reset TOTP" danger onClick={() => pick('totp_reset')} />
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  danger,
  disabled,
}: Readonly<{
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}>): JSX.Element {
  const enabledColor = danger ? tokens.errorText : tokens.textPrimary;
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className="w-full text-left px-3 py-2 text-sm"
      style={{
        color: disabled ? tokens.disabledText : enabledColor,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
//  ActionDialog — single modal that swaps body per action kind.
// ─────────────────────────────────────────────────────────────────

const EMPTY_USER_LIST: AdminUserSummary[] = [];

export function ActionDialog({
  state,
  activeAgents = EMPTY_USER_LIST,
  activeManagers = EMPTY_USER_LIST,
  onClose,
  onDone,
}: Readonly<{
  state: ActionState;
  activeAgents?: AdminUserSummary[];
  activeManagers?: AdminUserSummary[];
  onClose: () => void;
  onDone: (msg: string) => void;
}>): JSX.Element {
  const popup = useMovablePopup<HTMLDialogElement>();
  const dialogRef = popup.panelRef;
  // Open via showModal() on mount. No close() in the cleanup: on a real unmount
  // React removes the <dialog> node (closing it natively), whereas a programmatic
  // close() fires the native 'close' event — and under React 18 StrictMode the
  // dev-only mount→unmount→remount routes that event to onClose, slamming the
  // modal shut the instant it opens. The `!open` guard stops the StrictMode
  // re-mount from calling showModal() on an already-open dialog (which throws).
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, [dialogRef]);

  const { kind, target } = state;
  const [reason, setReason] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [newRole, setNewRole] = useState<StaffRole>(
    ROLE_OPTIONS.find((r) => r !== target.role) ?? 'agent',
  );
  const [newTimezone, setNewTimezone] = useState<string>(
    target.timezone ?? 'America/Mexico_City',
  );
  const [reassignTo, setReassignTo] = useState<string>('');
  const [selectedManager, setSelectedManager] = useState<string>(
    target.manager_id ?? '',
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const meta = describe(kind, fullName(target));
  // A change to or from manager or super admin is a proposal a different super admin confirms.
  const proposes = proposesChange(kind, target, newRole);
  const proposedKind = PROPOSED_KIND[kind];
  // Changing a role, retiring a second factor and every proposal take a fresh authenticator code.
  const requiresStepUp = kind === 'change_role' || kind === 'totp_reset' || proposes;

  const propose = async (changeKind: StaffChangeKind): Promise<void> => {
    await adminService.proposeStaffChange({
      kind: changeKind,
      target_user_id: target.id,
      ...(changeKind === 'change_role' ? { new_role: newRole } : {}),
      reason,
      mfa_code: mfaCode,
    });
    onDone(PROPOSED_MESSAGE);
  };

  const submit = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      if (proposes && proposedKind) {
        await propose(proposedKind);
        return;
      }
      let resp;
      switch (kind) {
        case 'deactivate':
          resp = await adminService.deactivateUser(target.id, {
            reason,
            reassign_clients_to: target.role === 'agent' && reassignTo ? reassignTo : null,
          });
          break;
        case 'activate':
          resp = await adminService.activateUser(target.id, reason);
          break;
        case 'force_password':
          resp = await adminService.forcePasswordChange(target.id, reason);
          break;
        case 'unlock':
          resp = await adminService.unlockAccount(target.id, reason);
          break;
        case 'revoke_sessions':
          resp = await adminService.revokeSessions(target.id, reason);
          break;
        case 'change_role':
          resp = await adminService.changeRole(target.id, {
            new_role: newRole,
            reason,
            mfa_code: mfaCode,
          });
          break;
        case 'reassign_clients':
          if (!reassignTo) {
            setErr('Select a target agent');
            setBusy(false);
            return;
          }
          resp = await adminService.reassignClients(target.id, {
            target_agent_id: reassignTo,
            reason,
          });
          break;
        case 'change_timezone':
          if (!newTimezone.trim()) {
            setErr('Pick a timezone');
            setBusy(false);
            return;
          }
          resp = await adminService.changeTimezone(target.id, {
            timezone: newTimezone.trim(),
            reason,
          });
          break;
        case 'totp_reset':
          resp = await adminService.resetTOTP(target.id, {
            reason,
            mfa_code: mfaCode,
          });
          break;
        case 'assign_manager':
          resp = await adminService.setManager(
            target.id,
            selectedManager || null,
            reason,
          );
          break;
      }
      onDone(resp.message ?? 'Done.');
    } catch (e) {
      setErr(requiresStepUp ? stepUpErrorMessage(e, extractError(e)) : extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitDisabled =
    busy ||
    reason.trim().length < 4 ||
    (requiresStepUp && !isMfaCode(mfaCode)) ||
    (kind === 'reassign_clients' && !reassignTo);
  const submitEnabledBg = meta.danger ? tokens.errorText : tokens.infoText;
  let submitLabel = proposes ? 'Propose' : meta.confirm;
  if (busy) submitLabel = 'Working…';

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onCancel={onClose}
      className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto backdrop:bg-black/45"
      style={{ ...popup.panelStyle, background: tokens.surface, border: 'none' }}
    >
        {popup.bar}
        <h2 className="text-lg font-semibold mb-1" style={{ color: tokens.textPrimary }}>
          {meta.title}
        </h2>
        <p className="text-sm mb-4" style={{ color: tokens.textMuted }}>{meta.subtitle}</p>
        {proposes && (
          <p className="text-sm mb-4" style={{ color: tokens.textMuted }}>
            This takes two super admins: your proposal waits until a different super admin
            confirms it with their code. The person it is about cannot decide on it.
          </p>
        )}

        {kind === 'change_role' && (
          <label className="block mb-3">
            <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>New role</span>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as StaffRole)}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${tokens.border}` }}
            >
              {ROLE_OPTIONS.filter((r) => r !== target.role).map((r) => (
                <option key={r} value={r}>{ROLE_LABEL[r]}</option>
              ))}
            </select>
          </label>
        )}

        {kind === 'change_timezone' && (
          <label className="block mb-3">
            <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>
              IANA timezone
            </span>
            <select
              value={COMMON_TIMEZONES.includes(newTimezone) ? newTimezone : ''}
              onChange={(e) => setNewTimezone(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm mb-2"
              style={{ border: `1px solid ${tokens.border}` }}
            >
              <option value="">— Choose from the shortlist —</option>
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            <input
              type="text"
              value={newTimezone}
              onChange={(e) => setNewTimezone(e.target.value)}
              placeholder="Or type any IANA zone, e.g. Europe/Madrid"
              maxLength={64}
              className="w-full px-3 py-2 rounded-lg text-sm font-mono"
              style={{ border: `1px solid ${tokens.border}` }}
            />
            <span className="block mt-1 text-xs" style={{ color: tokens.textMuted }}>
              Current: {target.timezone ?? <em>not set</em>}
            </span>
          </label>
        )}

        {(kind === 'reassign_clients' || (kind === 'deactivate' && target.role === 'agent')) && (
          <label className="block mb-3">
            <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>
              {kind === 'deactivate' ? 'Reassign clients to (optional)' : 'Target agent'}
            </span>
            <select
              value={reassignTo}
              onChange={(e) => setReassignTo(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${tokens.border}` }}
            >
              <option value="">— Select agent —</option>
              {activeAgents
                .filter((a) => a.id !== target.id)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {fullName(a)} ({a.email})
                  </option>
                ))}
            </select>
          </label>
        )}

        {kind === 'assign_manager' && (
          <label className="block mb-3">
            <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>
              Manager
            </span>
            <select
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${tokens.border}` }}
            >
              <option value="">— No manager (fan-out fallback) —</option>
              {activeManagers.map((m) => (
                <option key={m.id} value={m.id}>
                  {fullName(m)} ({m.email})
                </option>
              ))}
            </select>
            <span className="block mt-1 text-xs" style={{ color: tokens.textMuted }}>
              Current: {target.manager_name ?? <em>none</em>}.
              {' '}
              Empty = rejection emails fan out to every active manager.
              {activeManagers.length === 0 && (
                <span style={{ color: tokens.errorText }}>
                  {' '}No active managers loaded on this page — filter to &quot;Manager&quot; first.
                </span>
              )}
            </span>
          </label>
        )}

        <label className="block mb-3">
          <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>
            Reason (mandatory · stored in audit log)
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${tokens.border}` }}
            placeholder="e.g. ticket SEC-1234 — agent off-boarding"
          />
        </label>

        {requiresStepUp && (
          <label className="block mb-3">
            <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>
              Confirm with the code from your authenticator app
            </span>
            <input
              value={mfaCode}
              onChange={(e) => setMfaCode(cleanMfaInput(e.target.value))}
              inputMode="numeric"
              autoComplete="one-time-code"
              className="w-40 px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${tokens.border}` }}
            />
          </label>
        )}

        {err && (
          <div
            className="mb-3 p-3 rounded-lg text-sm"
            style={{ background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`, color: tokens.errorText }}
          >
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: tokens.surfaceMuted, color: tokens.textPrimary }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitDisabled}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{
              background: submitDisabled ? tokens.disabledBg : submitEnabledBg,
              color: submitDisabled ? tokens.disabledText : '#FFFFFF',
              cursor: submitDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            {submitLabel}
          </button>
        </div>
    </dialog>
  );
}

function describe(
  kind: ActionKind,
  name: string,
): { title: string; subtitle: string; confirm: string; danger?: boolean } {
  switch (kind) {
    case 'deactivate':
      return {
        title: `Deactivate ${name}`,
        subtitle: 'Soft-disable the account, revoke active sessions, and (for agents) optionally reassign their clients.',
        confirm: 'Deactivate',
        danger: true,
      };
    case 'activate':
      return {
        title: `Activate ${name}`,
        subtitle: 'Restore login access. Existing audit history is preserved.',
        confirm: 'Activate',
      };
    case 'force_password':
      return {
        title: `Force password change for ${name}`,
        subtitle: 'On next login the user will be required to set a new password.',
        confirm: 'Force change',
      };
    case 'unlock':
      return {
        title: `Unlock ${name}`,
        subtitle: 'Reset failed-attempt counter and clear lockout.',
        confirm: 'Unlock',
      };
    case 'revoke_sessions':
      return {
        title: `Revoke all sessions for ${name}`,
        subtitle: 'All active refresh tokens will be invalidated. The user must sign in again.',
        confirm: 'Revoke sessions',
      };
    case 'change_role':
      return {
        title: `Change role · ${name}`,
        subtitle: 'Sessions will be revoked. Requires a code from your authenticator app.',
        confirm: 'Change role',
        danger: true,
      };
    case 'reassign_clients':
      return {
        title: `Reassign clients of ${name}`,
        subtitle: 'Bulk-move every client currently assigned to this agent to another active agent.',
        confirm: 'Reassign',
      };
    case 'change_timezone':
      return {
        title: `Change timezone · ${name}`,
        subtitle: 'Agents never edit their own TZ — scheduling trusts this row, so drift would silently break slot rendering.',
        confirm: 'Change timezone',
      };
    case 'assign_manager':
      return {
        title: `Assign manager · ${name}`,
        subtitle:
          'Set or clear the reporting manager. With a manager set, rejection-of-onboarding emails route to that one person; empty falls back to fanning out to every active manager.',
        confirm: 'Save',
      };
    case 'totp_reset':
      return {
        title: `Reset TOTP for ${name}`,
        subtitle:
          'Removes every second factor: the authenticator, all passkeys and any unused recovery codes. The user will be asked to set them up again on next login. Requires a code from your authenticator app.',
        confirm: 'Reset TOTP',
        danger: true,
      };
  }
}
